import { Component, OnInit, OnDestroy } from '@angular/core';
import { PrimaryFunctions } from '@xbs/xbs-enums';

import {
  ClosableModalWithErrorResponseModel
} from '../../../shared/components/base/closable-modal-with-error-response-model';
import { NotificationService, PbasService, PrimaryFunctionsService } from '../../../shared/services';
import { ModalService, ConfirmationModalComponent } from '../../../shared/modules/modal';
import {
  TestedPartyModalPayload,
  TestedParty,
  PrimaryFunction,
  SegmentTypeEnum,
  TestedPartyErrorResponse,
  Message
} from '../../../shared/models';
import { TinyTextEditor, TranslationConstants } from '../../../shared/constants';

@Component({
  selector: 'tested-party-modal',
  templateUrl: './tested-party-modal.component.html',
})
export class TestedPartyModalComponent
  extends ClosableModalWithErrorResponseModel<TestedPartyErrorResponse>
  implements OnInit, OnDestroy {
  data: TestedPartyModalPayload;
  success: Function;
  testedParty: TestedParty = new TestedParty();
  segmentTypes: string[] = [];
  primaryFunctions: PrimaryFunction[] = [];
  shouldShowOtherPrimaryFunction: boolean = false;
  testedPartyPrimaryFunctionsDropDown: string[] = [PrimaryFunctions.ByName.Distributor.Name, PrimaryFunctions.ByName.ServiceProvider.Name, PrimaryFunctions.ByName.Manufacturer.Name, PrimaryFunctions.ByName.DistributorOther.Name, PrimaryFunctions.ByName.NonValueAddedDistributor.Name, PrimaryFunctions.ByName.Retail.Name, PrimaryFunctions.ByName.ServiceProviderOther.Name, PrimaryFunctions.ByName.ValueAddedDistributor.Name];
  testedPartyOtherPrimaryFunctions: string[] = [PrimaryFunctions.ByName.DistributorOther.Name, PrimaryFunctions.ByName.ServiceProviderOther.Name];

  tinyInit = TinyTextEditor.INIT;

  initialPrimaryFunction: PrimaryFunction = new PrimaryFunction();

  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _pbaService: PbasService,
    private _primaryFunctionsService: PrimaryFunctionsService
  ) {
    super(notificationService, modalService);
    this.testedParty.primaryFunction = new PrimaryFunction();
  }

  ngOnInit(): void {
    this.listenForErrorMessages();
    this.getPrimaryFunctions();
    this.segmentTypes = this.getSegmentTypeEnumValues();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  save(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    if (
      this.data.isEdit &&
      this.testedParty.primaryFunction.primaryFunctionId !== this.initialPrimaryFunction.primaryFunctionId
    ) {
      this.openConfirmationModalIfPrimaryFunctionChanged();
    } else {
      this.saveTestedParty();
    }
  }

  getSegmentTypeValueByTypeString(segmentTypeString: string): SegmentTypeEnum {
    return +SegmentTypeEnum[segmentTypeString];
  }

  getSegmentTypeLabel(segmentTypeString: string): string {
    return this._pbaService.getSegmentTypeLabel(segmentTypeString);
  }

  selectTestedPartyPrimaryFunction() {
    const currentPrimaryFunctionId = this.testedParty.primaryFunction.primaryFunctionId;
    const currentPrimaryFunction = this.primaryFunctions.filter(primaryFunction => primaryFunction.primaryFunctionId === currentPrimaryFunctionId);
    this.testedParty.primaryFunction = { ...currentPrimaryFunction[0] };
    if (this.testedPartyOtherPrimaryFunctions.includes(this.testedParty.primaryFunction.name)) {
      this.shouldShowOtherPrimaryFunction = true;
      this.testedParty.otherPrimaryFunction = '';
    } else {
      this.shouldShowOtherPrimaryFunction = false;
      this.testedParty.otherPrimaryFunction = null;
    }
  }

  private saveTestedParty() {
    this.resetErrors();

    if (this.shouldShowOtherPrimaryFunction) {
      if (!this.testedParty.otherPrimaryFunction) {
        this.testedParty.otherPrimaryFunction = '';
      }
    } else {
      this.testedParty.otherPrimaryFunction = null;
    }

    this._pbaService.saveTestedParty(this.data.pba, this.testedParty, this.data.isEdit).subscribe(() => {
      this.closeAllModals();
      this.success();
    });
  }

  // tslint:disable-next-line
  private openConfirmationModalIfPrimaryFunctionChanged() {
    this.modalService.openModal({
      component: ConfirmationModalComponent,
      data: {
        title: TranslationConstants.GLOBAL_CONFIRM,
        content: TranslationConstants.TESTED_PARTY_CHANGE_PRIMARY_FUNCTION,
      } as Message,
      success: () => {
        this.saveTestedParty();
      },
      cancel: () => {
        this.isSaveDisabled = false;
      },
    });
  }

  private getSegmentTypeEnumValues(): string[] {
    const enumValues = Object.values(SegmentTypeEnum);
    const nonNumberEnumValues = enumValues.filter(enumvalue => !isNaN(Number(SegmentTypeEnum[enumvalue]))).sort((seg1, seg2) => { return seg1 > seg2 ? 1 : -1; }) as string[];
    return nonNumberEnumValues;
  }

  private getPrimaryFunctions(): void {
    this._primaryFunctionsService.list().subscribe(({ data }) => {
      // remove the following filter() func once we
      // have support in Comp Search for Manufacturers, etc.
      this.primaryFunctions = data.filter(primaryFunction => this.testedPartyPrimaryFunctionsDropDown.includes(primaryFunction.name));
      if (this.data.isEdit) {
        this.getTestedParty();
      }
    });
  }

  private getTestedParty(): void {
    this._pbaService.getTestedParty(this.data.pba.pbaId).subscribe(({ data }) => {
      this.testedParty = data;
      this.initialPrimaryFunction = { ...this.testedParty.primaryFunction };
      if (this.testedPartyOtherPrimaryFunctions.includes(this.initialPrimaryFunction.name)) {
        this.shouldShowOtherPrimaryFunction = true;
      }
    });
  }
}
